import { Complement, ComplementGroup, Menu, MenuItem, MenuTypes } from '@app/types';
import { MenuItemFavorited, MenuItemWithDetails, MenuItemWithDetailsOfBag } from '@app/types/menu-item';
import { deepCloneObject } from './object';

export function getComplementsSelectedOf(group: ComplementGroup) {
  const complements = [];
  for (const complement of group.complementos || []) {
    if ((complement?.amount ?? 0) > 0) {
      complements.push(complement);
    }
  }
  return complements;
}

export function getAllComplementsSelectedOf(item: MenuItem) {
  const complements = [];
  for (const group of item.gruposDeComplementos || []) {
    for (const complement of group.complementos || []) {
      if ((complement.amount ?? 0) > 0) {
        complements.push(complement);
      }
    }
  }
  return complements;
}

export function getSelectedComplementMoreExpensive(complements: Complement[] = []) {
  let moreExpensive = null;
  for (let complement of complements) {
    if (complement.preco > (moreExpensive?.preco ?? 0) && (complement?.amount ?? 0) > 0) {
      moreExpensive = complement;
    }
  }
  return moreExpensive;
}

export function getAmountComplementSelected(group: ComplementGroup) {
  return group.complementos?.reduce((total, complement) => {
    return total + (complement.amount ?? 0);
  }, 0);
}

export function isGroupValidOnBasisOfComplementsSelected(
  group: ComplementGroup,
  amountComplementSelectedParam?: number
) {
  const amountComplementSelected = amountComplementSelectedParam ?? getAmountComplementSelected(group);

  if (group.opcional) return amountComplementSelected > 0;

  return amountComplementSelected >= group.minimo && amountComplementSelected <= group.maximo;
}

export function isMenuItemsBagEquals(item: MenuItemWithDetailsOfBag, itemToCompare: MenuItemWithDetailsOfBag) {
  if (item.id !== itemToCompare.id || item.observations?.trim() !== itemToCompare.observations?.trim()) return false;

  const itemComplements = getSelectedComplements(item);
  const itemToCompareComplements = getSelectedComplements(itemToCompare);

  const itemComplementsAmountMapped = itemComplements.reduce<Record<string, number>>((previous, current) => {
    return Object.assign(previous, { [current.id]: current.amount });
  }, {});

  return (
    itemComplements.length === itemToCompareComplements.length &&
    itemToCompareComplements.every((complement) => complement.amount === itemComplementsAmountMapped[complement.id])
  );

  function getSelectedComplements(i: MenuItem) {
    return i.gruposDeComplementos.reduce((previous, current) => {
      previous.push(...current.complementos.filter((c) => !!c.amount));
      return previous;
    }, [] as Complement[]);
  }
}

type MenuItemWithDetailsConstructor = {
  menuItem: MenuItem;
  menu: Menu;
};

type MenuItemFavoritedConstructor = {
  menuItem: MenuItem;
  menu: Menu;
};

type MenuItemWithDetailsOfBagConstructor = {
  menuItem: MenuItemWithDetails;
  observations: string;
  amount: number;
  bagItemId: string;
};

export class MenuItemFactory {
  static createMenuItemWithDetails(data: MenuItemWithDetailsConstructor): MenuItemWithDetails {
    return {
      ...deepCloneObject(data.menuItem),
      menuName: data.menu.nome,
      isPizzaItem: data.menu.tipoMenu === MenuTypes.Pizza,
    };
  }

  static createMenuItemFavorited(data: MenuItemFavoritedConstructor): MenuItemFavorited {
    return {
      ...deepCloneObject(data.menuItem),
      menuName: 'Favoritos',
      isPizzaItem: data.menu.tipoMenu === MenuTypes.Pizza,
      isFavorited: true,
      originalMenuName: data.menu.nome,
      nameWithMenuLabel: `${data.menu.nome} (${data.menuItem.nome})`,
    };
  }

  static createMenuItemWithDetailsOfBag(data: MenuItemWithDetailsOfBagConstructor): MenuItemWithDetailsOfBag {
    const { menuItem, amount, observations, bagItemId } = data;
    return {
      ...deepCloneObject(menuItem),
      observations,
      amount,
      bagItemId,
    };
  }
}
